import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { StatusBadge } from "../components/Badges";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const STATUS_COLORS = {
  active: { bg: "var(--success-soft)", color: "var(--success)" },
  withdrawn: { bg: "var(--danger-soft)", color: "var(--danger)" },
  expired: { bg: "var(--bg-2)", color: "var(--muted)" },
  pending_renewal: { bg: "var(--warn-soft)", color: "var(--warn)" },
  replaced_by_newer_version: { bg: "var(--primary-soft)", color: "var(--primary)" },
  revoked_by_admin: { bg: "var(--danger-soft)", color: "var(--danger)" },
};

function StatusPill({ status }) {
  const t = STATUS_COLORS[status] || STATUS_COLORS.expired;
  return (
    <span data-testid={`status-${status}`}
      className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {status.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

function SdkSnippets({ apiUrl }) {
  const [lang, setLang] = useState("curl");
  const [copied, setCopied] = useState("");
  const base = `${apiUrl}/api/public/v1/consents`;
  const snippets = {
    curl: {
      collect:
`curl -X POST ${base}/collect \\
  -H "X-Api-Key: dpdp_<prefix>_<secret>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "template_id": "TEMPLATE_ID",
    "principal_id": "USER_ID_IN_YOUR_APP",
    "principal_email": "user@example.com",
    "source_application": "checkout-web",
    "channel": "website",
    "device_info": { "platform": "web" }
  }'
# → { id, status, template_version, granted_at, expires_at, proof_hash }`,
      status:
`curl -G ${base}/status \\
  -H "X-Api-Key: dpdp_<prefix>_<secret>" \\
  --data-urlencode "principal_id=USER_ID_IN_YOUR_APP" \\
  --data-urlencode "template_id=TEMPLATE_ID"
# → { items: [{ id, status, template_version, granted_at, expires_at, ... }], count }`,
      withdraw:
`curl -X POST ${base}/withdraw \\
  -H "X-Api-Key: dpdp_<prefix>_<secret>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "consent_id": "CONSENT_ID_FROM_COLLECT",
    "reason": "user_unsubscribed"
  }'
# → { id, status: "withdrawn", withdrawn_at }`,
      renew:
`# Renew accepts the latest published version of the same template on
# behalf of the principal — use after you publish a new template version.
curl -X POST ${base}/renew \\
  -H "X-Api-Key: dpdp_<prefix>_<secret>" \\
  -H "Content-Type: application/json" \\
  -d '{ "consent_id": "CONSENT_ID_TO_RENEW" }'
# → { id: NEW_CONSENT_ID, template_version, granted_at, proof_hash }`,
      evidence:
`# Full tamper-evident evidence bundle (SHA-256 chain, IP-masked, UA hash).
# Requires scope: consent.evidence
curl -G ${base}/CONSENT_ID/evidence \\
  -H "X-Api-Key: dpdp_<prefix>_<secret>"
# → { consent, template, chain: [{ event, proof_hash, previous_hash, ... }] }`,
      template:
`# User-facing template metadata (title, notice, purpose, retention). Used by
# the drop-in widget and any custom UI you build.
curl -G ${base}/templates/TEMPLATE_ID \\
  -H "X-Api-Key: dpdp_<prefix>_<secret>"
# → { id, title, plain_language_notice, purpose_name, data_categories,
#     retention_period_days, language, channel, version, status }`,
    },
    node: {
      collect:
`import fetch from "node-fetch";

const res = await fetch("${base}/collect", {
  method: "POST",
  headers: {
    "X-Api-Key": process.env.DPDP_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    template_id: "TEMPLATE_ID",
    principal_id: userId,
    principal_email: userEmail,
    source_application: "checkout-web",
    channel: "website",
    device_info: { platform: "web" },
  }),
});
const consent = await res.json();
// consent.id, consent.template_version, consent.proof_hash`,
      status:
`const res = await fetch(
  "${base}/status?principal_id=" + encodeURIComponent(userId) +
  "&template_id=" + encodeURIComponent(templateId),
  { headers: { "X-Api-Key": process.env.DPDP_API_KEY } }
);
const { items } = await res.json();
const active = items.find(c => c.status === "active");`,
      withdraw:
`await fetch("${base}/withdraw", {
  method: "POST",
  headers: {
    "X-Api-Key": process.env.DPDP_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    consent_id: consentId,
    reason: "user_unsubscribed",
  }),
});`,
      renew:
`// Ask the principal to re-accept when you publish a new template version.
await fetch("${base}/renew", {
  method: "POST",
  headers: {
    "X-Api-Key": process.env.DPDP_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ consent_id: consentId }),
});`,
      evidence:
`// Requires scope: consent.evidence
const res = await fetch("${base}/" + consentId + "/evidence", {
  headers: { "X-Api-Key": process.env.DPDP_API_KEY },
});
const { consent, template, chain } = await res.json();`,
      template:
`// Fetch user-facing template metadata to render your own consent UI.
const res = await fetch("${base}/templates/" + templateId, {
  headers: { "X-Api-Key": process.env.DPDP_API_KEY },
});
const template = await res.json();
// template.title, template.plain_language_notice, template.purpose_name`,
    },
    python: {
      collect:
`import os, requests

resp = requests.post(
    "${base}/collect",
    headers={"X-Api-Key": os.environ["DPDP_API_KEY"]},
    json={
        "template_id": "TEMPLATE_ID",
        "principal_id": user_id,
        "principal_email": user_email,
        "source_application": "checkout-web",
        "channel": "website",
        "device_info": {"platform": "web"},
    },
    timeout=10,
)
resp.raise_for_status()
consent = resp.json()
# consent["id"], consent["template_version"], consent["proof_hash"]`,
      status:
`resp = requests.get(
    "${base}/status",
    headers={"X-Api-Key": os.environ["DPDP_API_KEY"]},
    params={"principal_id": user_id, "template_id": template_id},
    timeout=10,
)
items = resp.json()["items"]
active = next((c for c in items if c["status"] == "active"), None)`,
      withdraw:
`requests.post(
    "${base}/withdraw",
    headers={"X-Api-Key": os.environ["DPDP_API_KEY"]},
    json={"consent_id": consent_id, "reason": "user_unsubscribed"},
    timeout=10,
).raise_for_status()`,
      renew:
`# Ask the principal to re-accept when you publish a new template version.
requests.post(
    "${base}/renew",
    headers={"X-Api-Key": os.environ["DPDP_API_KEY"]},
    json={"consent_id": consent_id},
    timeout=10,
).raise_for_status()`,
      evidence:
`# Requires scope: consent.evidence
resp = requests.get(
    f"${base}/{consent_id}/evidence",
    headers={"X-Api-Key": os.environ["DPDP_API_KEY"]},
    timeout=10,
)
bundle = resp.json()
# bundle["consent"], bundle["template"], bundle["chain"]`,
      template:
`# Fetch user-facing template metadata to render your own consent UI.
resp = requests.get(
    f"${base}/templates/{template_id}",
    headers={"X-Api-Key": os.environ["DPDP_API_KEY"]},
    timeout=10,
)
template = resp.json()`,
    },
  };
  const sections = [
    { key: "template", label: "GET /templates/{id} — fetch user-facing notice for your UI" },
    { key: "collect",  label: "POST /collect — record a new consent" },
    { key: "status",   label: "GET /status — check current consents for a principal" },
    { key: "withdraw", label: "POST /withdraw — withdraw an active consent" },
    { key: "renew",    label: "POST /renew — re-accept the latest template version" },
    { key: "evidence", label: "GET /{consent_id}/evidence — tamper-evident audit bundle" },
  ];
  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key); setTimeout(() => setCopied(""), 1200);
  };
  return (
    <div className="surface rounded-xl p-5 mb-5" style={{ border: "1px solid var(--border)" }} data-testid="sdk-snippets">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[13px] font-semibold ink">Integrate the public Consent API</div>
          <div className="text-[11.5px] muted mt-0.5">
            Full lifecycle — collect · check · withdraw · renew · evidence. Add <code className="mono">X-Api-Key</code> header on every request. Base URL: <code className="mono">{base}</code>
          </div>
        </div>
        <div className="flex gap-1 surface rounded-lg p-1" style={{ border: "1px solid var(--border)" }}>
          {["curl", "node", "python"].map((l) => (
            <button
              key={l}
              data-testid={`sdk-lang-${l}`}
              onClick={() => setLang(l)}
              className="px-2.5 py-1 text-[11px] font-medium rounded-md"
              style={{
                background: lang === l ? "var(--primary-soft)" : "transparent",
                color: lang === l ? "var(--primary)" : "var(--muted)",
              }}
            >
              {l === "curl" ? "cURL" : l === "node" ? "Node.js" : "Python"}
            </button>
          ))}
        </div>
      </div>
      {sections.map((s) => (
        <div key={s.key} className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider muted">{s.label}</div>
            <button onClick={() => copy(snippets[lang][s.key], `${lang}-${s.key}`)}
              className="text-[11px] font-medium hover:underline flex items-center gap-1"
              style={{ color: "var(--primary)" }}
              data-testid={`sdk-copy-${lang}-${s.key}`}>
              <Icons.Copy className="w-3 h-3" />
              {copied === `${lang}-${s.key}` ? "Copied!" : "Copy"}
            </button>
          </div>
          <pre className="mono text-[11px] leading-relaxed p-3 rounded-md overflow-x-auto"
            style={{ background: "var(--bg-2)", color: "var(--ink)" }}>
            {snippets[lang][s.key]}
          </pre>
        </div>
      ))}
    </div>
  );
}

function WidgetEmbed({ apiUrl }) {
  const [copied, setCopied] = useState("");
  const widgetSrc = `${apiUrl}/api/public/v1/consents/widget.js`;
  const embedHtml =
`<!-- 1. Add the widget script anywhere in your page (loads async, ~5 KB gz) -->
<script src="${widgetSrc}"></script>

<!-- 2. Initialise it with your publishable API key + template ID -->
<script>
  DPDPConsent.init({
    apiKey:      "dpdp_<prefix>_<secret>",     // create in the API Keys section below
    templateId:  "TEMPLATE_ID",                // click any active template card to copy
    principalId: window.currentUserId,         // optional — falls back to a cookie
    principalEmail: window.currentUserEmail,   // optional
    theme:       "auto",                       // "auto" | "light" | "dark"
    autoShow:    true,                         // pops open if consent missing/expired
    onGranted:   (c)  => { /* enable analytics / marketing here */ },
    onWithdrawn: ()   => { /* disable tracking, purge caches */ },
    onError:     (e)  => console.warn("DPDPConsent error:", e),
  });

  // Optional: expose a "Manage my consent" link anywhere on your page
  // <a onclick="DPDPConsent.open('manage')">Manage consent</a>
</script>`;

  const reactSnippet =
`import { useEffect } from "react";

export function DPDPConsentBoot({ userId, userEmail, templateId }) {
  useEffect(() => {
    const s = document.createElement("script");
    s.src = "${widgetSrc}";
    s.onload = () => window.DPDPConsent.init({
      apiKey: process.env.NEXT_PUBLIC_DPDP_API_KEY,
      templateId,
      principalId: userId,
      principalEmail: userEmail,
      theme: "auto",
      autoShow: true,
      onGranted:   (c) => window.dispatchEvent(new CustomEvent("dpdp:granted", { detail: c })),
      onWithdrawn: ()  => window.dispatchEvent(new CustomEvent("dpdp:withdrawn")),
    });
    document.body.appendChild(s);
    return () => s.remove();
  }, [userId, userEmail, templateId]);
  return null;
}`;

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key); setTimeout(() => setCopied(""), 1200);
  };

  return (
    <div className="surface rounded-xl p-5 mb-5" style={{ border: "1px solid var(--border)" }} data-testid="widget-embed">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "linear-gradient(135deg, #4F46E5, #7C3AED)", color: "white" }}>
            <Icons.PanelTop className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[13px] font-semibold ink">Drop-in Consent Popup Widget</div>
            <div className="text-[11.5px] muted mt-0.5 max-w-2xl leading-relaxed">
              A branded, DPDP-compliant modal that renders your template notice, collects grant/withdraw, and handles the full lifecycle.
              Two script tags — no UI to build. Auto-shows only when consent is missing, expired, or replaced by a newer version.
            </div>
          </div>
        </div>
        <span className="px-2 py-1 rounded-md text-[10.5px] font-semibold uppercase tracking-wider"
          style={{ background: "var(--success-soft)", color: "var(--success)" }}>
          Ready to embed
        </span>
      </div>

      <div className="grid gap-3 mb-4 sm:grid-cols-3">
        {[
          { icon: "ShieldCheck", label: "DPDP Sec. 6 compliant",     desc: "Notice, purpose, retention, withdrawal — all shown" },
          { icon: "Fingerprint", label: "Tamper-evident proof",  desc: "SHA-256 chain, IP-mask, UA-hash on every grant" },
          { icon: "MousePointerClick", label: "Zero UI to build", desc: "Themable via CSS vars, dark/light auto-detect" },
        ].map((f) => {
          const I = Icons[f.icon] || Icons.Check;
          return (
            <div key={f.label} className="p-3 rounded-lg" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold ink">
                <I className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /> {f.label}
              </div>
              <div className="text-[11px] muted mt-1 leading-relaxed">{f.desc}</div>
            </div>
          );
        })}
      </div>

      {[
        { key: "html",  label: "HTML embed — paste into any website (2 script tags)", code: embedHtml },
        { key: "react", label: "React / Next.js — drop-in component", code: reactSnippet },
      ].map((s) => (
        <div key={s.key} className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider muted">{s.label}</div>
            <button
              onClick={() => copy(s.code, s.key)}
              className="text-[11px] font-medium hover:underline flex items-center gap-1"
              style={{ color: "var(--primary)" }}
              data-testid={`widget-copy-${s.key}`}
            >
              <Icons.Copy className="w-3 h-3" />
              {copied === s.key ? "Copied!" : "Copy"}
            </button>
          </div>
          <pre className="mono text-[11px] leading-relaxed p-3 rounded-md overflow-x-auto"
            style={{ background: "var(--bg-2)", color: "var(--ink)" }}>
            {s.code}
          </pre>
        </div>
      ))}

      <details className="mt-3">
        <summary className="text-[11.5px] font-semibold ink cursor-pointer hover:opacity-80">
          Config reference — all init options
        </summary>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-[11.5px]">
            <thead>
              <tr style={{ background: "var(--bg-2)" }}>
                {["Option", "Type", "Default", "Description"].map(h => (
                  <th key={h} className="text-left px-2.5 py-1.5 text-[10.5px] font-semibold uppercase tracking-wider muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ["apiKey",         "string",   "—",         "Publishable API key with consent.collect + consent.status + consent.withdraw scopes."],
                ["templateId",     "string",   "—",         "UUID of the active consent template to collect against."],
                ["principalId",    "string",   "cookie",    "Your app's user id. If omitted, an anonymous UUID is stored in a 'dpdp_pid' cookie."],
                ["principalEmail", "string",   "null",      "Optional — lets the user manage their consent via the Data Principal Portal."],
                ["sourceApplication", "string", "location.host", "Free-form label for filtering in your dashboards (e.g. 'checkout-web')."],
                ["theme",          "string",   "\"auto\"",  "\"auto\" | \"light\" | \"dark\" — auto matches prefers-color-scheme."],
                ["autoShow",       "boolean",  "true",      "If true, opens modal automatically when consent is missing/expired."],
                ["onReady",        "fn(state)","—",         "Fires after boot with { status: 'active'|'missing', consent? }."],
                ["onGranted",      "fn(consent)","—",       "Fires after successful collect — enable your tracking here."],
                ["onWithdrawn",    "fn(result)","—",        "Fires after successful withdraw — disable tracking here."],
                ["onError",        "fn(err)",  "—",         "Any API failure surfaces here."],
              ].map(row => (
                <tr key={row[0]} style={{ borderTop: "1px solid var(--border)" }}>
                  {row.map((c, i) => (
                    <td key={i} className={`px-2.5 py-1.5 align-top ${i < 2 ? "mono" : ""}`}>
                      <span className={i === 3 ? "muted" : "ink"}>{c}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[11px] muted">
          Runtime methods: <code className="mono">DPDPConsent.open(&quot;collect&quot;|&quot;manage&quot;)</code>, <code className="mono">.close()</code>, <code className="mono">.status()</code>, <code className="mono">.grant()</code>, <code className="mono">.withdraw()</code>, <code className="mono">.principalId()</code>.
        </div>
      </details>

      <div className="mt-3 p-2.5 rounded-md text-[11px] flex items-start gap-2" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>
        <Icons.ShieldAlert className="w-3.5 h-3.5 mt-[1px] shrink-0" />
        <div>
          <strong>Security note:</strong> the API key is embedded in browser JS. Scope it to <code className="mono">consent.collect</code>, <code className="mono">consent.status</code> and <code className="mono">consent.withdraw</code> only — <em>never</em> grant <code className="mono">consent.evidence</code> to a widget key. Rotate the key if you suspect leakage.
        </div>
      </div>
    </div>
  );
}

const emptyTemplate = {
  title: "",
  plain_language_notice: "",
  purpose_name: "",
  data_categories: [],
  third_party_sharing: { shared: false, details: "" },
  retention_period_days: 365,
  withdrawal_process: "Email privacy@example.com or use the unsubscribe link in your account.",
  grievance_officer: { name: "", email: "", phone: "" },
  user_acceptance_text: "I consent to the processing of my personal data as described.",
  language: "English",
  channel: "website",
};

export default function ConsentManager() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "org_admin", "dpo"].includes(user?.role);

  const [tab, setTab] = useState("dashboard");
  const [dashboard, setDashboard] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [consents, setConsents] = useState([]);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const [tplModal, setTplModal] = useState(false);
  const [tplMode, setTplMode] = useState("create"); // create | edit | publish
  const [tplForm, setTplForm] = useState(emptyTemplate);
  const [editingTpl, setEditingTpl] = useState(null);

  const [keyModal, setKeyModal] = useState(false);
  const [newKeyForm, setNewKeyForm] = useState({ name: "", scopes: ["consent.collect", "consent.status", "consent.withdraw", "consent.evidence"], expires_days: null });
  const [issuedKey, setIssuedKey] = useState(null);

  const [evidence, setEvidence] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [d, t, c, k] = await Promise.all([
        api.get("/consent-mgmt/dashboard"),
        api.get("/consent-mgmt/templates"),
        api.get(`/consent-mgmt/consents?limit=100${statusFilter ? `&status=${statusFilter}` : ""}`),
        isAdmin ? api.get("/consent-mgmt/api-keys") : Promise.resolve({ data: { items: [] } }),
      ]);
      setDashboard(d.data);
      setTemplates(t.data.items || []);
      setConsents(c.data.items || []);
      setKeys(k.data.items || []);
    } catch (e) { toast.error("Failed to load consent data"); }
    setLoading(false);
  };
  useEffect(() => { load();   }, [statusFilter]);

  const saveTemplate = async () => {
    try {
      if (tplMode === "create") {
        await api.post("/consent-mgmt/templates", tplForm);
        toast.success("Template created (v1.0)");
      } else if (tplMode === "publish") {
        const { data } = await api.post(`/consent-mgmt/templates/${editingTpl.id}/publish-new-version`, tplForm);
        toast.success(`Published v${data.version} — existing consents flagged for renewal`);
      } else {
        await api.put(`/consent-mgmt/templates/${editingTpl.id}`, tplForm);
        toast.success("Template updated");
      }
      setTplModal(false); setEditingTpl(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const revokeConsent = async (c) => {
    const reason = window.prompt("Reason (optional):", "");
    try {
      await api.post(`/consent-mgmt/consents/${c.id}/withdraw`, { reason });
      toast.success("Consent revoked");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  const viewEvidence = async (c) => {
    try {
      const { data } = await api.get(`/consent-mgmt/consents/${c.id}/evidence`);
      setEvidence({ ...data, consent_id: c.id });
    } catch { toast.error("Failed to fetch evidence"); }
  };

  const downloadProofPack = async (cid) => {
    try {
      const res = await api.get(`/consent-mgmt/consents/${cid}/proof-pack`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `consent-proof-pack-${cid}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Proof-pack downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
  };

  const issueKey = async () => {
    try {
      const { data } = await api.post("/consent-mgmt/api-keys", newKeyForm);
      setIssuedKey(data);
      setNewKeyForm({ name: "", scopes: newKeyForm.scopes, expires_days: null });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const revokeKey = async (k) => {
    if (!window.confirm(`Revoke key "${k.name}"?`)) return;
    try {
      await api.post(`/consent-mgmt/api-keys/${k.id}/revoke`);
      toast.success("API key revoked"); await load();
    } catch { toast.error("Failed"); }
  };

  const openCreateTpl = () => { setTplMode("create"); setEditingTpl(null); setTplForm(emptyTemplate); setTplModal(true); };
  const openEditTpl = (t) => { setTplMode("edit"); setEditingTpl(t); setTplForm({ ...emptyTemplate, ...t, data_categories: t.data_categories || [] }); setTplModal(true); };
  const openPublishTpl = (t) => { setTplMode("publish"); setEditingTpl(t); setTplForm({ ...emptyTemplate, ...t, data_categories: t.data_categories || [] }); setTplModal(true); };

  const trend = dashboard?.trend_30d || [];
  const maxTrend = Math.max(1, ...trend.map(d => (d.granted || 0) + (d.withdrawn || 0) + (d.expired || 0)));

  if (loading && !dashboard) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Consent Manager"
        kicker="DPDP Sec. 6 Consent Lifecycle"
        description="Templates, versioning, external API, tamper-linked evidence, expiry & renewal — end-to-end consent lifecycle for DPDP compliance."
        testId="consent-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={load} data-testid="consent-refresh">
              <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Btn>
            {isAdmin && tab === "templates" && (
              <Btn onClick={openCreateTpl} data-testid="tpl-new-btn">
                <Icons.Plus className="w-3.5 h-3.5" /> New template
              </Btn>
            )}
            {isAdmin && tab === "api-keys" && (
              <Btn onClick={() => setKeyModal(true)} data-testid="apikey-new-btn">
                <Icons.KeyRound className="w-3.5 h-3.5" /> Issue API key
              </Btn>
            )}
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "templates", label: "Templates", icon: "FileCheck" },
          { key: "consents", label: "Consents", icon: "ShieldCheck" },
          { key: "api-keys", label: "API keys", icon: "KeyRound", admin: true },
        ].filter(t => !t.admin || isAdmin).map((t) => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button
              key={t.key}
              data-testid={`consent-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{
                background: tab === t.key ? "var(--primary-soft)" : "transparent",
                color: tab === t.key ? "var(--primary)" : "var(--muted)",
              }}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dashboard && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Total consents" value={dashboard.total_consents} testId="stat-total" />
            <StatCard label="Active" value={dashboard.by_status?.active || 0} tone="success" testId="stat-active" />
            <StatCard label="Withdrawn" value={dashboard.by_status?.withdrawn || 0} tone="danger" testId="stat-withdrawn" />
            <StatCard label="Expired" value={dashboard.by_status?.expired || 0} testId="stat-expired" />
            <StatCard label="Renewals due (30d)" value={dashboard.renewals_due_30d || 0} tone="warn" testId="stat-renewals" />
            <StatCard label="Replaced by new version" value={dashboard.by_status?.replaced_by_newer_version || 0} tone="primary" testId="stat-replaced" />
            <StatCard label="Revoked by admin" value={dashboard.by_status?.revoked_by_admin || 0} tone="danger" testId="stat-revoked" />
            <StatCard label="Consent gaps" value={dashboard.consent_gaps?.length || 0} tone="warn" testId="stat-gaps"
              hint="Activities requiring consent without a template" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Trend — last 30 days</div>
              {trend.length === 0 ? (
                <div className="text-[12px] muted">No consent events yet.</div>
              ) : (
                <div className="flex items-end gap-1 h-40" data-testid="consent-trend">
                  {trend.map((d, i) => {
                    const g = d.granted || 0, w = d.withdrawn || 0, e = d.expired || 0;
                    const total = g + w + e;
                    const h = (total / maxTrend) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: granted ${g}, withdrawn ${w}, expired ${e}`}>
                        <div className="w-full flex flex-col justify-end" style={{ height: "100%" }}>
                          <div style={{ height: `${h * (g / (total || 1))}%`, background: "var(--success)" }} />
                          <div style={{ height: `${h * (w / (total || 1))}%`, background: "var(--danger)" }} />
                          <div style={{ height: `${h * (e / (total || 1))}%`, background: "var(--muted)" }} />
                        </div>
                        {i % 5 === 0 && <div className="text-[9px] muted">{d.day.slice(5)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-3 flex items-center gap-3 text-[10.5px] muted">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "var(--success)" }} /> Granted</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "var(--danger)" }} /> Withdrawn</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "var(--muted)" }} /> Expired</span>
              </div>
            </div>

            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Purpose-wise consents</div>
              {(dashboard.by_purpose || []).length === 0 ? (
                <div className="text-[12px] muted">No data yet.</div>
              ) : (
                <div className="space-y-2">
                  {dashboard.by_purpose.map((p) => (
                    <div key={p.name} className="flex items-center gap-2">
                      <div className="flex-1 text-[12px] ink truncate">{p.name}</div>
                      <div className="text-[12px] muted">{p.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Application-wise consents</div>
              {(dashboard.by_application || []).length === 0 ? (
                <div className="text-[12px] muted">No data yet.</div>
              ) : (
                <div className="space-y-2">
                  {dashboard.by_application.map((p) => (
                    <div key={p.name} className="flex items-center gap-2">
                      <div className="flex-1 text-[12px] ink truncate">{p.name}</div>
                      <div className="text-[12px] muted">{p.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Consent gaps</div>
              {(dashboard.consent_gaps || []).length === 0 ? (
                <div className="text-[12px] muted">All consent-required activities have an active template ✅</div>
              ) : (
                <div className="space-y-2" data-testid="consent-gaps-list">
                  {dashboard.consent_gaps.map((g) => (
                    <div key={g.activity_id} className="flex items-center gap-2 p-2 rounded-md" style={{ background: "var(--warn-soft)" }}>
                      <Icons.AlertTriangle className="w-4 h-4 shrink-0" style={{ color: "var(--warn)" }} />
                      <div className="flex-1">
                        <div className="text-[12px] font-medium ink">{g.activity_name}</div>
                        <div className="text-[10.5px] muted">{g.reason}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "templates" && (
        templates.length === 0 ? (
          <EmptyState
            title="No consent templates yet"
            hint="Create your first template to start capturing DPDP-compliant consent."
            action={isAdmin && <Btn onClick={openCreateTpl}><Icons.Plus className="w-4 h-4" /> Create template</Btn>}
          />
        ) : (
          <div className="grid gap-3" data-testid="templates-list">
            {templates.map((t) => (
              <div key={t.id} className="surface rounded-xl p-4"
                style={{ border: "1px solid var(--border)" }}
                data-testid={`tpl-${t.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold ink text-[14px]">{t.title}</div>
                      <span className="px-2 py-[3px] rounded-md text-[10.5px] font-medium" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                        v{t.version}
                      </span>
                      <StatusBadge status={t.status} />
                      <span className="text-[11px] muted">· {t.language} · {t.channel}</span>
                    </div>
                    <div className="text-[12px] muted mt-1 line-clamp-2">{t.plain_language_notice}</div>
                    <div className="mt-2 flex items-center gap-3 flex-wrap text-[11px] muted">
                      <span><Icons.Target className="w-3 h-3 inline mr-1" />{t.purpose_name || "—"}</span>
                      <span><Icons.Database className="w-3 h-3 inline mr-1" />{(t.data_categories || []).length} categories</span>
                      <span><Icons.Timer className="w-3 h-3 inline mr-1" />{t.retention_period_days || "—"} days</span>
                      {t.previous_version_id && <span className="text-[10.5px]" style={{ color: "var(--primary)" }}>← from prev v</span>}
                    </div>
                    {/* Template ID — for external / API integrations */}
                    <button
                      type="button"
                      data-testid={`tpl-copy-id-${t.id}`}
                      onClick={() => {
                        navigator.clipboard.writeText(t.id);
                        toast.success("Template ID copied");
                      }}
                      title="Click to copy this template_id for API integration"
                      className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10.5px] font-mono transition-colors hover:bg-app-2"
                      style={{ background: "var(--bg-2)", border: "1px solid var(--border)", color: "var(--muted)" }}
                    >
                      <Icons.Hash className="w-3 h-3" />
                      <span className="ink">template_id:</span>
                      <span className="select-all">{t.id}</span>
                      <Icons.Copy className="w-3 h-3 opacity-60" />
                    </button>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Btn variant="secondary" size="sm" onClick={() => openEditTpl(t)} data-testid={`tpl-edit-${t.id}`}>
                        <Icons.Edit3 className="w-3.5 h-3.5" /> Edit
                      </Btn>
                      {t.status === "active" && (
                        <Btn size="sm" onClick={() => openPublishTpl(t)} data-testid={`tpl-publish-${t.id}`}>
                          <Icons.GitBranchPlus className="w-3.5 h-3.5" /> Publish new version
                        </Btn>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === "consents" && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <SelectInput data-testid="consent-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">All statuses</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </SelectInput>
          </div>
          {consents.length === 0 ? (
            <EmptyState title="No consents yet" hint="Consents captured via the external API or admin will appear here." />
          ) : (
            <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-2)" }}>
                  <tr>
                    {["Principal", "Purpose", "Version", "Status", "Source", "Granted", "Actions"].map((h) => (
                      <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="consents-tbody">
                  {consents.map((c) => (
                    <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`consent-${c.id}`}>
                      <td className="px-4 py-2 ink text-[12px]">
                        <div>{c.principal_id}</div>
                        <div className="text-[10.5px] muted">{c.principal_email}</div>
                      </td>
                      <td className="px-4 py-2 ink text-[12px]">{c.purpose_name || "—"}</td>
                      <td className="px-4 py-2 ink text-[12px]">v{c.template_version}</td>
                      <td className="px-4 py-2"><StatusPill status={c.status} /></td>
                      <td className="px-4 py-2 muted text-[11px]">{c.source_application || "—"}</td>
                      <td className="px-4 py-2 muted text-[11px]">{c.granted_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => viewEvidence(c)} className="text-[11px] font-medium mr-2 hover:underline" style={{ color: "var(--primary)" }} data-testid={`evidence-${c.id}`}>
                          Evidence
                        </button>
                        {isAdmin && ["active", "pending_renewal"].includes(c.status) && (
                          <button onClick={() => revokeConsent(c)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }} data-testid={`revoke-${c.id}`}>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "api-keys" && (
        <>
          <WidgetEmbed apiUrl="https://api.dpdpcrm.com" />
          <SdkSnippets apiUrl="https://api.dpdpcrm.com" />
          {keys.length === 0 ? (
            <EmptyState
              title="No API keys yet"
              hint="Issue keys so external apps can collect + check consent via the public API."
              action={isAdmin && <Btn onClick={() => setKeyModal(true)}><Icons.Plus className="w-4 h-4" /> Issue API key</Btn>}
            />
          ) : (
          <div className="grid gap-3" data-testid="api-keys-list">
            {keys.map((k) => (
              <div key={k.id} className="surface rounded-xl p-4 flex items-center gap-4"
                style={{ border: "1px solid var(--border)" }}
                data-testid={`apikey-${k.id}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                  <Icons.KeyRound className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold ink text-[13.5px]">{k.name}</div>
                    <span className="font-mono text-[11px] muted">dpdp_{k.prefix}_••••</span>
                    {!k.is_active && <StatusBadge status="revoked" />}
                  </div>
                  <div className="text-[11px] muted mt-1 flex items-center gap-3 flex-wrap">
                    <span>Scopes: {(k.scopes || []).join(", ")}</span>
                    <span>Last used: {k.last_used_at?.slice(0, 16).replace("T", " ") || "never"}</span>
                    {k.expires_at && <span>Expires: {k.expires_at.slice(0, 10)}</span>}
                  </div>
                </div>
                {isAdmin && k.is_active && (
                  <Btn variant="secondary" size="sm" onClick={() => revokeKey(k)} data-testid={`apikey-revoke-${k.id}`}>
                    <Icons.XCircle className="w-3.5 h-3.5" /> Revoke
                  </Btn>
                )}
              </div>
            ))}
          </div>
          )}
        </>
      )}

      {/* Template modal */}
      <Dialog open={tplModal} onOpenChange={setTplModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {tplMode === "create" ? "New consent template"
                : tplMode === "publish" ? `Publish new version of "${editingTpl?.title}"`
                : `Edit "${editingTpl?.title}"`}
            </DialogTitle>
            <DialogDescription>
              {tplMode === "publish"
                ? "Existing consents on the current version will be flagged 'Replaced by newer version' and principals invited to renew."
                : "DPDP Sec. 5 + Sec. 6 mandatory fields — all captured below."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[62vh] overflow-y-auto">
            <Field label="Title" required className="col-span-2">
              <TextInput data-testid="tpl-title" value={tplForm.title} onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} />
            </Field>
            <Field label="Plain-language notice" required className="col-span-2">
              <TextArea data-testid="tpl-notice" value={tplForm.plain_language_notice} onChange={(e) => setTplForm({ ...tplForm, plain_language_notice: e.target.value })} placeholder="Explain in simple terms what data you collect and why." />
            </Field>
            <Field label="Purpose of processing">
              <TextInput data-testid="tpl-purpose" value={tplForm.purpose_name || ""} onChange={(e) => setTplForm({ ...tplForm, purpose_name: e.target.value })} placeholder="e.g. Marketing communication" />
            </Field>
            <Field label="Data categories (comma-sep)">
              <TextInput data-testid="tpl-categories" value={(tplForm.data_categories || []).join(", ")}
                onChange={(e) => setTplForm({ ...tplForm, data_categories: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
            </Field>
            <Field label="Retention (days)">
              <TextInput type="number" data-testid="tpl-retention" value={tplForm.retention_period_days || ""} onChange={(e) => setTplForm({ ...tplForm, retention_period_days: Number(e.target.value) || null })} />
            </Field>
            <Field label="Language">
              <SelectInput data-testid="tpl-language" value={tplForm.language} onChange={(e) => setTplForm({ ...tplForm, language: e.target.value })}>
                {["English", "Hindi", "Tamil", "Telugu", "Bengali", "Marathi", "Gujarati", "Kannada", "Malayalam"].map(l => <option key={l} value={l}>{l}</option>)}
              </SelectInput>
            </Field>
            <Field label="Channel">
              <SelectInput data-testid="tpl-channel" value={tplForm.channel} onChange={(e) => setTplForm({ ...tplForm, channel: e.target.value })}>
                {["website", "mobile_app", "email", "sms", "paper", "api", "call_center"].map(c => <option key={c} value={c}>{c}</option>)}
              </SelectInput>
            </Field>
            <Field label="Third-party sharing" className="col-span-2">
              <div className="grid grid-cols-2 gap-2">
                <SelectInput data-testid="tpl-sharing" value={tplForm.third_party_sharing?.shared ? "yes" : "no"}
                  onChange={(e) => setTplForm({ ...tplForm, third_party_sharing: { ...tplForm.third_party_sharing, shared: e.target.value === "yes" } })}>
                  <option value="no">No — data stays with us</option>
                  <option value="yes">Yes — shared with third parties</option>
                </SelectInput>
                {tplForm.third_party_sharing?.shared && (
                  <TextInput data-testid="tpl-sharing-details" value={tplForm.third_party_sharing.details || ""}
                    onChange={(e) => setTplForm({ ...tplForm, third_party_sharing: { ...tplForm.third_party_sharing, details: e.target.value } })}
                    placeholder="Which third parties?" />
                )}
              </div>
            </Field>
            <Field label="Withdrawal process" className="col-span-2">
              <TextArea data-testid="tpl-withdrawal" value={tplForm.withdrawal_process} onChange={(e) => setTplForm({ ...tplForm, withdrawal_process: e.target.value })} />
            </Field>
            <Field label="Grievance officer name">
              <TextInput data-testid="tpl-grievance-name" value={tplForm.grievance_officer?.name || ""} onChange={(e) => setTplForm({ ...tplForm, grievance_officer: { ...tplForm.grievance_officer, name: e.target.value } })} />
            </Field>
            <Field label="Grievance officer email">
              <TextInput data-testid="tpl-grievance-email" value={tplForm.grievance_officer?.email || ""} onChange={(e) => setTplForm({ ...tplForm, grievance_officer: { ...tplForm.grievance_officer, email: e.target.value } })} />
            </Field>
            <Field label="User acceptance checkbox text" className="col-span-2">
              <TextInput data-testid="tpl-acceptance" value={tplForm.user_acceptance_text} onChange={(e) => setTplForm({ ...tplForm, user_acceptance_text: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setTplModal(false)} data-testid="tpl-cancel">Cancel</Btn>
            <Btn onClick={saveTemplate} data-testid="tpl-save">
              {tplMode === "create" ? "Create v1.0" : tplMode === "publish" ? "Publish new version" : "Save changes"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Key modal */}
      <Dialog open={keyModal} onOpenChange={(v) => { setKeyModal(v); if (!v) setIssuedKey(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{issuedKey ? "API key issued" : "Issue new API key"}</DialogTitle>
            <DialogDescription>
              {issuedKey ? "Copy the key now — it will not be shown again." : "Keys authenticate external apps calling /api/public/v1/consents/*."}
            </DialogDescription>
          </DialogHeader>
          {issuedKey ? (
            <div className="space-y-3">
              <div className="p-3 rounded-md font-mono text-[12px] break-all" style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="issued-key">
                {issuedKey.plaintext_key}
              </div>
              <div className="p-3 rounded-md text-[12px]" style={{ background: "var(--warn-soft)", color: "var(--warn)" }}>
                <Icons.AlertTriangle className="w-4 h-4 inline mr-1" /> {issuedKey.warning}
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              <Field label="Key name" required>
                <TextInput data-testid="key-name" value={newKeyForm.name} onChange={(e) => setNewKeyForm({ ...newKeyForm, name: e.target.value })} placeholder="e.g. Marketing website" />
              </Field>
              <Field label="Scopes">
                <div className="grid grid-cols-2 gap-2">
                  {["consent.collect", "consent.status", "consent.withdraw", "consent.evidence"].map(s => (
                    <label key={s} className="flex items-center gap-2 text-sm p-2 rounded-md" style={{ border: "1px solid var(--border)" }}>
                      <input data-testid={`scope-${s}`} type="checkbox"
                        checked={newKeyForm.scopes.includes(s)}
                        onChange={(e) => setNewKeyForm({ ...newKeyForm, scopes: e.target.checked ? [...newKeyForm.scopes, s] : newKeyForm.scopes.filter(x => x !== s) })} />
                      <span className="font-mono text-[11px]">{s}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Expires in (days, optional)">
                <TextInput type="number" data-testid="key-expires" value={newKeyForm.expires_days || ""}
                  onChange={(e) => setNewKeyForm({ ...newKeyForm, expires_days: Number(e.target.value) || null })} placeholder="Leave blank for no expiry" />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="secondary" onClick={() => { setKeyModal(false); setIssuedKey(null); }} data-testid="key-close">
              {issuedKey ? "Done" : "Cancel"}
            </Btn>
            {!issuedKey && (
              <Btn onClick={issueKey} disabled={!newKeyForm.name} data-testid="key-issue">
                <Icons.KeyRound className="w-3.5 h-3.5" /> Issue key
              </Btn>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Evidence viewer */}
      <Dialog open={!!evidence} onOpenChange={(v) => !v && setEvidence(null)}>
        <DialogContent className="max-w-3xl">
          {evidence && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Consent evidence
                  <span className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-semibold rounded-md"
                    style={{ background: evidence.chain_valid ? "var(--success-soft)" : "var(--danger-soft)",
                             color: evidence.chain_valid ? "var(--success)" : "var(--danger)" }}
                    data-testid="chain-status">
                    <Icons.Link className="w-3 h-3" />
                    {evidence.chain_valid ? "Hash chain intact" : "Chain broken"}
                  </span>
                </DialogTitle>
                <DialogDescription>
                  Tamper-linked audit trail across all lifecycle events for this consent.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[62vh] overflow-y-auto space-y-3">
                <div className="p-3 rounded-md" style={{ background: "var(--bg-2)" }}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-1">Template</div>
                  <div className="text-[12px] ink"><b>{evidence.template.title}</b> v{evidence.template.version} · {evidence.template.language}</div>
                  <div className="text-[11px] muted mt-1">{evidence.template.plain_language_notice}</div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-1">Audit trail</div>
                  <div className="space-y-1.5" data-testid="audit-trail">
                    {evidence.audit_trail.map((e, i) => (
                      <div key={i} className="p-2 rounded-md flex items-start gap-3" style={{ border: "1px solid var(--border)" }}>
                        <div className="text-[11px] muted shrink-0 mono">{e.created_at?.slice(0, 19).replace("T", " ")}</div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] ink font-medium">{e.action}
                            <span className="text-[10.5px] muted ml-2">by {e.actor_type}</span>
                          </div>
                          <div className="text-[10.5px] muted mono">
                            hash: {e.proof_hash?.slice(0, 24)}…
                            {e.previous_hash && <span> ← prev: {e.previous_hash.slice(0, 12)}…</span>}
                          </div>
                          {e.source_application && <div className="text-[10.5px] muted">src: {e.source_application}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Btn variant="secondary" onClick={() => downloadProofPack(evidence.consent_id)} data-testid="evidence-download-zip">
                  <Icons.Download className="w-3.5 h-3.5" /> Download proof-pack (.zip)
                </Btn>
                <Btn variant="secondary" onClick={() => setEvidence(null)} data-testid="evidence-close">Close</Btn>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
